import { useEffect, useState } from 'react';

import { Link, useLocation, useNavigate } from '@tanstack/react-router';

import { Icon } from '@/components/icon/Icon';
import { useAuthStore } from '@/features/auth/auth-store';
import { navBadgeCounts, useReferralsStore } from '@/features/referrals/referrals-store';
import { cn } from '@/lib/utils';

import { canActOnReferrals, type DisplayRole } from '@/features/auth/roles';
import { openDocumentation } from '@/features/docs/open-docs';
import { NAV_ID_TO_PATH, PATH_TO_NAV_ID, type NavId } from '@/router/paths';

import { FOOTER_SETTINGS_ITEM, NAV_GROUPS, NAV_ITEMS, SETTINGS_ROLES } from './nav-items';

/** localStorage key for the persisted collapsed state (SCRUM-282). */
const COLLAPSE_KEY = 'bankiq-sidenav-collapsed';

/**
 * Read the initial collapsed state from localStorage. Mirrors the privacy-mode
 * safe pattern used by ThemeToggle: any read failure falls back to expanded.
 */
function readInitialCollapsed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    // localStorage may be unavailable in privacy mode; default to expanded.
    return false;
  }
}

/**
 * Side-navigation rail — collapsible column to the left of <main>, matching
 * Ryan Lynch's app-shell.html prototype (the canonical design reference).
 *
 * Structure (SCRUM-282, v6 IA per SCRUM-322):
 *   - Items are grouped under "HOME" and "PLATFORM" section headers
 *     (NAV_GROUPS order); membership comes from each NavItem.group. The
 *     pre-v6 ADMIN section was removed — Settings now renders in the footer.
 *   - Active item: gold text + gold-dim background + a 2px gold left-accent bar
 *     (the `pl-[10px]` offsets the 2px border so text x-position is unchanged
 *     between idle and active). Hover: text-0 on a bg-3 background.
 *   - A bottom footer (pushed down by a flex spacer) holds the functional
 *     Collapse toggle, the Settings item, and Sign Out (SCRUM-298).
 *
 * Collapse behavior: clicking Collapse shrinks the rail to 56px (icon-only;
 * labels + section headers become sr-only so accessible names survive), rotates
 * the chevron 180°, and persists the boolean to localStorage (COLLAPSE_KEY) so
 * the choice survives reloads.
 *
 * Active-state (which item is selected) is now derived from the URL via
 * TanStack Router (SCRUM-616): each nav item is a <Link>, and the active item
 * is the one whose path matches the current location. This replaced the
 * activeId/onNavigate prop contract from the router-less AppShell.
 */
export function SideNav() {
  const [collapsed, setCollapsed] = useState<boolean>(readInitialCollapsed);
  const signOut = useAuthStore((s) => s.signOut);
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  // Active nav id is derived from the current URL path (SCRUM-616).
  const pathname = useLocation({ select: (l) => l.pathname });
  const activeId: NavId | undefined = PATH_TO_NAV_ID[pathname];
  // Referrals nav badge (SCRUM-322/327 — v6 `updateReferralsNavDot`):
  // personally-assigned open referrals render the filled "N direct" pill;
  // otherwise a plain gold unread accent marks new referrals in the queue.
  const referrals = useReferralsStore((s) => s.referrals);
  // v7 role visibility (SCRUM-387): filter items by the active display role.
  const role = (user?.role ?? 'Bank Executive') as DisplayRole;
  // Referral badge is ACTORS-only (v7 updateReferralsNavDot, SCRUM-388):
  // tellers/admins never see the direct pill or the unread accent.
  const badgeCounts = navBadgeCounts(referrals, user?.email);
  const mineCount = canActOnReferrals(role) ? badgeCounts.mineCount : 0;
  const pendingCount = canActOnReferrals(role) ? badgeCounts.pendingCount : 0;
  const referralUnreadCount = mineCount || pendingCount;
  const visibleItems = NAV_ITEMS.filter((item) => item.roles.includes(role));
  const showSettings = SETTINGS_ROLES.includes(role);

  useEffect(() => {
    try {
      window.localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch {
      // localStorage write failures (privacy mode, quota) are non-fatal.
    }
  }, [collapsed]);

  return (
    <nav
      aria-label="Sections"
      className={cn(
        'bg-bg-1 border-border-0 py-tk-xl flex flex-shrink-0 flex-col overflow-x-hidden overflow-y-auto border-r transition-[width] duration-200',
        collapsed ? 'w-14' : 'w-56',
      )}
    >
      {NAV_GROUPS.filter((group) => visibleItems.some((i) => i.group === group.id)).map(
        (group, groupIndex) => (
          <div key={group.id} className="contents">
            <p
              className={cn(
                'text-text-3 px-tk-md pb-tk-sm font-mono text-[0.625rem] font-bold tracking-[0.10em] uppercase',
                groupIndex === 0 ? 'pt-0' : 'pt-tk-lg',
                collapsed && 'sr-only',
              )}
            >
              {group.label}
            </p>
            {visibleItems
              .filter((item) => item.group === group.id)
              .map((item) => {
                const isActive = item.id === activeId;
                // Unread accent: only the Referrals item, only when NOT active
                // (v6 suppresses the accent on the active page), only when there
                // is something to action.
                const hasUnread = item.id === 'referrals' && !isActive && referralUnreadCount > 0;
                const showDirectPill = item.id === 'referrals' && mineCount > 0 && !collapsed;
                return (
                  <Link
                    key={item.id}
                    to={NAV_ID_TO_PATH[item.id as NavId]}
                    aria-current={isActive ? 'page' : undefined}
                    title={
                      collapsed
                        ? item.label
                        : // Referral count tooltip (SCRUM-478): matches the prototype's
                          // updateReferralsNavDot — the title shows whenever the count
                          // is non-zero (INCLUDING while the item is active; only the
                          // left-accent styling is inactive-gated) and singularizes.
                          item.id === 'referrals' && referralUnreadCount > 0
                          ? mineCount > 0
                            ? `${String(mineCount)} referral${mineCount === 1 ? '' : 's'} assigned to you`
                            : `${String(pendingCount)} new referral${pendingCount === 1 ? '' : 's'}`
                          : undefined
                    }
                    className={cn(
                      // Row scale/weight per client feedback (SCRUM-846): 0.8125rem
                      // -> 0.875rem (13px -> 14px, "a point larger") and weight
                      // 400 -> 500 ("slightly bolder"). Applied uniformly to all
                      // five row kinds (nav item, Collapse, Settings, Docs, Sign
                      // Out). Safe for the ACTIVE row: active state is signalled by
                      // colour/background/left accent border, never by weight, so a
                      // uniform 500 does not flatten the active/idle contrast.
                      'gap-tk-sm rounded-tk-sm mx-tk-sm flex items-center py-[7px] text-[0.875rem] font-medium transition-colors',
                      collapsed ? 'justify-center px-0' : 'px-tk-md',
                      isActive && 'bg-brand-dim text-brand-primary border-brand-primary border-l-2',
                      isActive && !collapsed && 'pl-[10px]',
                      !isActive && 'text-text-1 hover:text-text-0 hover:bg-bg-3',
                      hasUnread && !collapsed && 'border-brand-primary border-l-2 pl-[10px]',
                    )}
                  >
                    <span className={cn('flex-shrink-0', isActive ? 'opacity-100' : 'opacity-70')}>
                      {item.iconName ? <Icon name={item.iconName} size={16} /> : null}
                    </span>
                    <span className={cn(collapsed && 'sr-only')}>{item.label}</span>
                    {showDirectPill && (
                      <span
                        className="bg-brand-primary rounded-tk-full ml-auto px-2 py-0.5 font-mono text-[0.625rem] font-bold text-white"
                        style={{ boxShadow: '0 0 0 3px var(--cyan-dim)' }}
                        aria-label={`${String(mineCount)} referral${mineCount === 1 ? '' : 's'} assigned to you`}
                      >
                        {mineCount} direct
                      </span>
                    )}
                  </Link>
                );
              })}
          </div>
        ),
      )}

      {/* Spacer pushes the footer to the bottom of the rail. */}
      <div className="flex-1" aria-hidden="true" />

      {/* Footer: Collapse toggle + Sign Out placeholder, separated by a border. */}
      <div className="border-border-0 pt-tk-md mt-tk-md border-t">
        <button
          type="button"
          onClick={() => {
            setCollapsed((v) => !v);
          }}
          aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          aria-expanded={!collapsed}
          className={cn(
            'gap-tk-sm rounded-tk-sm mx-tk-sm text-text-2 hover:text-text-0 hover:bg-bg-3 flex items-center py-[7px] text-[0.875rem] font-medium transition-colors',
            collapsed ? 'justify-center px-0' : 'px-tk-md',
          )}
        >
          {/* Chevron — verbatim from app-shell.html; rotates 180° when collapsed. */}
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.25"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className={cn(
              'flex-shrink-0 transition-transform duration-200',
              collapsed && 'rotate-180',
            )}
          >
            <polyline points="10 4 6 8 10 12" />
          </svg>
          <span className={cn(collapsed && 'sr-only')}>Collapse</span>
        </button>

        {/* Settings — footer-rail item per the v6 shell (SCRUM-322). v7
            (SCRUM-387) gates it to SETTINGS_ROLES — Bank Executive plus Bank
            Admin, whose module grants are identical (SCRUM-913). */}
        {showSettings && (
          <Link
            to={NAV_ID_TO_PATH.settings}
            aria-current={activeId === FOOTER_SETTINGS_ITEM.id ? 'page' : undefined}
            title={collapsed ? FOOTER_SETTINGS_ITEM.label : undefined}
            className={cn(
              'gap-tk-sm rounded-tk-sm mx-tk-sm flex items-center py-[7px] text-[0.875rem] font-medium transition-colors',
              collapsed ? 'justify-center px-0' : 'px-tk-md',
              activeId === FOOTER_SETTINGS_ITEM.id &&
                'bg-brand-dim text-brand-primary border-brand-primary border-l-2',
              activeId === FOOTER_SETTINGS_ITEM.id && !collapsed && 'pl-[10px]',
              activeId !== FOOTER_SETTINGS_ITEM.id && 'text-text-1 hover:text-text-0 hover:bg-bg-3',
            )}
          >
            <span
              className={cn(
                'flex-shrink-0',
                activeId === FOOTER_SETTINGS_ITEM.id ? 'opacity-100' : 'opacity-70',
              )}
            >
              <Icon name={FOOTER_SETTINGS_ITEM.iconName} size={16} />
            </span>
            <span className={cn(collapsed && 'sr-only')}>{FOOTER_SETTINGS_ITEM.label}</span>
          </Link>
        )}

        {/* Documentation (SCRUM-731, ADR-013) — opens the docs site in a new
            tab. Visible to every role: bank users land on the public client
            guides; Platform Admins mint a docs session first (HttpOnly cookie
            scoped to /docs/internal) and land on the internal docs. The
            popup-blocker-safe open + fail-open-to-public logic lives in
            openDocumentation. */}
        <button
          type="button"
          onClick={() => {
            void openDocumentation(role);
          }}
          aria-label="Open documentation"
          title={collapsed ? 'Documentation' : undefined}
          className={cn(
            'gap-tk-sm rounded-tk-sm mx-tk-sm text-text-1 hover:text-text-0 hover:bg-bg-3 flex items-center py-[7px] text-[0.875rem] font-medium transition-colors',
            collapsed ? 'justify-center px-0' : 'px-tk-md',
          )}
        >
          {/* Open book — matches the 16px 1.25-stroke icon set used by the
              footer rail (Collapse chevron / Sign Out door). */}
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.25"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className="flex-shrink-0"
          >
            <path d="M8 3.5C7 2.5 5.5 2 3.5 2H2v10.5h2c1.7 0 3 .4 4 1.5 1-1.1 2.3-1.5 4-1.5h2V2h-1.5c-2 0-3.5.5-4.5 1.5Z" />
            <path d="M8 3.5V14" />
          </svg>
          <span className={cn(collapsed && 'sr-only')}>Documentation</span>
        </button>

        {/* Sign Out — clears the session and routes to /login (SCRUM-298). The
            store's signOut clears in-memory auth; navigate('/login') moves the
            URL so the guard sees an unauthenticated context (SCRUM-616). The
            real OIDC end-session (RP-initiated logout) is wired in SCRUM-102. */}
        <button
          type="button"
          onClick={() => {
            signOut();
            void navigate({ to: '/login' });
          }}
          aria-label="Sign out"
          title={collapsed ? 'Sign out' : undefined}
          className={cn(
            'gap-tk-sm rounded-tk-sm mx-tk-sm text-text-1 hover:text-text-0 hover:bg-bg-3 flex items-center py-[7px] text-[0.875rem] font-medium transition-colors',
            collapsed ? 'justify-center px-0' : 'px-tk-md',
          )}
        >
          {/* Door + arrow — verbatim from app-shell.html. */}
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.25"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className="flex-shrink-0"
          >
            <path d="M6 14H3.5C2.67 14 2 13.33 2 12.5V3.5C2 2.67 2.67 2 3.5 2H6" />
            <polyline points="10.5 11.5 14 8 10.5 4.5" />
            <line x1="14" y1="8" x2="6" y2="8" />
          </svg>
          <span className={cn(collapsed && 'sr-only')}>Sign Out</span>
        </button>
      </div>
    </nav>
  );
}
